import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import PartnerLoginPage from "@/pages/PartnerLoginPage";
import DemoLoginPage from "@/pages/DemoLoginPage";
import CustomPartnerLoginPage from "@/pages/CustomPartnerLoginPage";
import ClientDashboard from "@/pages/ClientDashboard";
import PartnerDashboard from "@/pages/PartnerDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import TermsPage from "@/pages/TermsPage";
import SignupPage from "@/pages/SignupPage";
import { AuthProvider, useAuth } from "@/context/AuthContext";

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard
    if (user.role === "client") return <Navigate to="/client" replace />;
    if (user.role === "partner") return <Navigate to="/partner" replace />;
    if (user.role === "subpartner") return <Navigate to="/subpartner" replace />;
    if (user.role === "admin") return <Navigate to="/admin" replace />;
  }
  
  return children;
};

const AppRoutes = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={`/${user.role}`} replace /> : <LoginPage />} />
      <Route path="/partnerlogin" element={user ? <Navigate to={`/${user.role}`} replace /> : <PartnerLoginPage />} />
      <Route path="/demo/login" element={user ? <Navigate to={`/${user.role}`} replace /> : <DemoLoginPage />} />
      <Route path="/:partnerCode/login" element={user ? <Navigate to={`/${user.role}`} replace /> : <CustomPartnerLoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/tnc" element={<TermsPage />} />
      <Route 
        path="/client/*" 
        element={
          <ProtectedRoute allowedRoles={["client"]}>
            <ClientDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/partner/*" 
        element={
          <ProtectedRoute allowedRoles={["partner"]}>
            <PartnerDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/subpartner/*" 
        element={
          <ProtectedRoute allowedRoles={["subpartner"]}>
            <PartnerDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/*" 
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route path="/" element={user ? <Navigate to={`/${user.role}`} replace /> : <Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
